import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ShieldAlert, Trash2, Mail, KeyRound, CheckCircle2, Loader2, FileText, Archive, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api } from "../App";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function AccountDeletionPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("email"); // email | code | done
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  const downloadExport = async () => {
    setExporting(true);
    try {
      const res = await api.post("/account-export/verify", { email, code }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "rivones-data-export.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("accountDeletion.exportReady"));
    } catch (err) {
      toast.error(t("accountDeletion.genericError"));
    } finally {
      setExporting(false);
    }
  };

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/account-deletion/request", { email });
      toast.success(t("accountDeletion.codeSent"));
      setStep("code");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("accountDeletion.genericError"));
    } finally {
      setBusy(false);
    }
  };

  const confirmDeletion = async (e) => {
    e.preventDefault();
    if (!window.confirm(t("accountDeletion.finalConfirm"))) return;
    setBusy(true);
    try {
      await api.post("/account-deletion/confirm", { email, code });
      setStep("done");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("accountDeletion.genericError"));
    } finally {
      setBusy(false);
    }
  };

  const deletedItems = t("accountDeletion.deletedItems", { returnObjects: true });
  const retainedItems = t("accountDeletion.retainedItems", { returnObjects: true });

  return (
    <div className="min-h-screen bg-sand" data-testid="account-deletion-page">
      <div className="max-w-3xl mx-auto px-4 py-10 sm:py-14">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 flex items-center gap-3">
              <Trash2 className="w-8 h-8 text-red-600 shrink-0" />
              {t("accountDeletion.title")}
            </h1>
            <p className="mt-2 text-sm text-neutral-500" data-testid="operator-identity">
              {t("accountDeletion.operator")}
            </p>
          </div>
          <LanguageSwitcher />
        </div>

        <div className="card-base mb-6">
          <p className="text-neutral-700">{t("accountDeletion.intro")}</p>
          <ol className="mt-4 space-y-2 text-sm text-neutral-700 list-decimal list-inside">
            <li>{t("accountDeletion.step1")}</li>
            <li>{t("accountDeletion.step2")}</li>
            <li>{t("accountDeletion.step3")}</li>
          </ol>
        </div>

        {/* Deletion form */}
        <div className="card-base border-red-200 mb-6" data-testid="deletion-form-card">
          <h2 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2 text-red-700">
            <ShieldAlert className="w-5 h-5" />
            {t("accountDeletion.formTitle")}
          </h2>

          {step === "email" && (
            <form onSubmit={requestCode} className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-neutral-700 flex items-center gap-1.5 mb-1.5">
                  <Mail className="w-4 h-4" /> {t("accountDeletion.emailLabel")}
                </label>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  data-testid="deletion-email-input"
                />
              </div>
              <Button type="submit" disabled={busy} className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="deletion-request-code-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                {t("accountDeletion.sendCode")}
              </Button>
            </form>
          )}

          {step === "code" && (
            <form onSubmit={confirmDeletion} className="space-y-4">
              <p className="text-sm text-neutral-600">{t("accountDeletion.codeHint", { email })}</p>
              <div>
                <label className="text-sm font-semibold text-neutral-700 flex items-center gap-1.5 mb-1.5">
                  <KeyRound className="w-4 h-4" /> {t("accountDeletion.codeLabel")}
                </label>
                <Input
                  required
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000"
                  className="tracking-[0.5em] text-center text-lg font-bold"
                  data-testid="deletion-code-input"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={downloadExport}
                disabled={exporting || code.length !== 6}
                className="w-full"
                data-testid="deletion-export-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                {t("accountDeletion.exportFirst")}
              </Button>
              <Button type="submit" disabled={busy || code.length !== 6} className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="deletion-confirm-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                {t("accountDeletion.confirmDelete")}
              </Button>
              <button type="button" onClick={() => setStep("email")} className="w-full text-sm text-neutral-500 hover:text-neutral-700" data-testid="deletion-back-btn">
                {t("accountDeletion.changeEmail")}
              </button>
            </form>
          )}

          {step === "done" && (
            <div className="text-center py-6" data-testid="deletion-success">
              <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-xl mb-2">{t("accountDeletion.doneTitle")}</h3>
              <p className="text-neutral-600 text-sm max-w-md mx-auto">{t("accountDeletion.doneBody")}</p>
            </div>
          )}
        </div>

        {/* What is deleted */}
        <div className="card-base mb-6" data-testid="deleted-data-section">
          <h2 className="font-heading font-semibold text-lg mb-3 flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-600" />
            {t("accountDeletion.deletedTitle")}
          </h2>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-neutral-700 list-disc list-inside">
            {Array.isArray(deletedItems) && deletedItems.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </div>

        {/* What is retained */}
        <div className="card-base mb-6" data-testid="retained-data-section">
          <h2 className="font-heading font-semibold text-lg mb-3 flex items-center gap-2">
            <Archive className="w-5 h-5 text-amber-600" />
            {t("accountDeletion.retainedTitle")}
          </h2>
          <p className="text-sm text-neutral-600 mb-3">{t("accountDeletion.retainedIntro")}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-neutral-200">
                  <th className="py-2 pr-4 font-semibold">{t("accountDeletion.retainedColData")}</th>
                  <th className="py-2 font-semibold">{t("accountDeletion.retainedColPeriod")}</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(retainedItems) && retainedItems.map((row, i) => (
                  <tr key={i} className="border-b border-neutral-100">
                    <td className="py-2 pr-4">{row.data}</td>
                    <td className="py-2 text-neutral-600">{row.period}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card-base" data-testid="deletion-legal-footer">
          <p className="text-xs text-neutral-500 flex items-start gap-2">
            <FileText className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              {t("accountDeletion.legalNote")}{" "}
              <a href="/privacidad" className="underline hover:text-neutral-700">{t("accountDeletion.privacyLink")}</a>
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
